import React from 'react'
import {Errors, withForm, Form, TextInput} from 'startlibs/lib/form'
import {withToggles} from 'startlibs/lib/hocs'
import {Dialog, Button, SplitColumnsContainer} from 'startlibs/lib/components'
import {
  buildValidation, confirmEmail,
  emailValidation,
  minLength,
  required,
  responseFailure
} from '../lib/validation'
import {formFetch, getFetcher} from 'startlibs'
import {NOT_ACTIVATED_ERROR} from '../signin/SignIn'
import {connect} from 'react-redux'
import {setNotifications, signIn} from '../reducers/index'
import styled from 'styled-components'
import {getColor} from 'startlibs/lib/lib'

const SignUpDialog = styled(Dialog)`
  max-width: 36rem;
  h2 {
    color: ${getColor('main')};
  }
  p {
    color: rgba(0,0,0,0.5);
    margin-top: -1.25rem;
    margin-bottom: 1.5rem;
    max-width: 28rem;
  }
`
const SubmitButtonContainer = styled.div`
  text-align: center;
  margin-top: 2rem;
  ${Button} {
    width: 12rem;
  }
`
const SignUpDialogFooter = styled.div`
  margin: 2rem -2rem -2rem;
  background-color: ${getColor('gray240')};
  padding: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  color: ${getColor('gray120')};
  border-bottom-left-radius: 6px;
  border-bottom-right-radius: 6px;
  border-top: 1px solid rgba(0,0,0,0.1);
  ${Button} {
    margin-left: 1rem;
  }
`
const ALREADY_REGISTERED = 'E-mail already registered.'
@withForm(formFetch)
@withToggles('signInToggle', 'loading')
@connect(undefined, {signIn,setNotifications})
export class CheckoutSignupDialog extends React.PureComponent {

  onFailure = responseFailure(this.props.form, ({detail, errorKey, message}) =>
    // sign up
    (errorKey === 'userexists' && {email: [ALREADY_REGISTERED]}) ||
    (errorKey === 'userNotActivated' && {email: [ALREADY_REGISTERED]}) ||
    (errorKey === 'userAccountRemoved' && {email: [`This account has been previously deleted. To reactivate the account please check ${this.props.form.properties.email} and click the reactivation link.`]}) ||
    (message === 'error.validation' && {email: ['Invalid e-mail.']}) ||
    // sign in
    (detail === 'Bad credentials' && {'': ['Invalid e-mail and password combination']}) ||
    (detail && detail.indexOf(' has not activated') >= 0 && {'username': [ALREADY_REGISTERED]}) ||
    (detail && detail.indexOf(' check your e-mail to reactivate') >= 0 && {'username': [`This account has been previously deleted. To reactivate the account please check ${this.props.form.properties.email} and click the reactivation link.`]})
  )

  preValidation = () => this.props.signInToggle.isOpen ? this.validateSignIn : this.validateSignUp

  validateSignIn = buildValidation({
    password: [required],
    email: [required]
  }, ({email, ...rest}) => ({...rest, username: email}))

  validateSignUp = buildValidation({
      confirmEmail: confirmEmail('email'),
      email: [emailValidation, required],
      firstName: [required],
      lastName: [required],
      password: [required, minLength(6)]
    },
    ({confirmEmail, email, ...rest}) => ({...rest, langKey: 'en', email, login: email})
  )

  componentDidUpdate(prevProps) {
    if (prevProps.signInToggle.isOpen !== this.props.signInToggle.isOpen) {
      this.props.form.connector.clearErrors()
    }
  }

  signInSuccess = () => {
    localStorage.setItem('hasLogged', 'true')
    this.props.loading.open()
    Promise.all([getFetcher('/api/account'),getFetcher('/api/stripe-customer')])
      .then(([account,stripeCustomer]) => {
        this.props.setStripCustomer(stripeCustomer)
        this.props.signIn(account)
        this.props.nextStep()
      })
  }

  signUpSuccess = () => {
    this.props.loading.open()
    localStorage.setItem('hasLogged', 'true')
    getFetcher('/api/account')
      .then((result) => {
        this.props.setStripCustomer(undefined,true)
        this.props.setNotifications("We sent an activation link to your email, please check it later.")
        this.props.signIn(result)
        this.props.nextStep()
      })

  }

  render() {
    const {closeDialog, form, loading, signInToggle} = this.props
    return !signInToggle.isOpen ? <SignUpDialog
        title="Sign up for Horos Cloud"
        closeDialog={closeDialog}
      >
        <Form
          alwaysSave
          preValidation={this.preValidation()}
          onSuccess={this.signUpSuccess}
          onFailure={this.onFailure}
          form={form}
          url='/api/register'
        >
          <Dialog.Content>
            <p>Create an account to proceed with your subscription, or <a className="link" onClick={signInToggle.toggle}>sign
              in</a> if you are already registered.</p>
            <SplitColumnsContainer>
              <TextInput
                path="firstName"
                form={form}
                placeholder="Ex: John"
                label="First name:"
              />
              <TextInput
                path="lastName"
                form={form}
                placeholder="Ex: Doe"
                label="Last name:"
              />
            </SplitColumnsContainer>
            <TextInput
              path="email"
              form={form}
              placeholder="Ex: example@email.com"
              label="Email:"
            />
            <TextInput
              path="confirmEmail"
              form={form}
              placeholder="Ex: example@email.com"
              label="Confirm email:"
            />

            <TextInput
              path="password"
              form={form}
              type="password"
              placeholder="Create a password"
              label="Password:"
            />
            <Errors form={form}/>
          </Dialog.Content>
          <SubmitButtonContainer>
            <Button highlight isLoading={form.isLoading || loading.isOpen} type="submit">Sign Up</Button>
          </SubmitButtonContainer>
          <SignUpDialogFooter>
            Already have an account?
            <Button outline disabled={form.isLoading || loading.isOpen} onClick={signInToggle.toggle}>Sign In</Button>
          </SignUpDialogFooter>
        </Form>
      </SignUpDialog> :
      <SignUpDialog
        title="Sign in to Horos Cloud"
        closeDialog={closeDialog}
      >
        <Form
          alwaysSave
          preValidation={this.preValidation()}
          onSuccess={this.signInSuccess}
          onFailure={this.onFailure}
          form={form}
          url='/api/authenticate'
        >
          <Dialog.Content>

            <TextInput
              path="email"
              form={form}
              placeholder="Your email"
              label="Email:"
            />

            <TextInput
              path="password"
              form={form}
              type="password"
              placeholder="Your password"
              label="Password:"
            />
            <Errors form={form}/>
          </Dialog.Content>
          <SubmitButtonContainer>
            <Button highlight type="submit" isLoading={form.isLoading || loading.isOpen}>Sign In</Button>
          </SubmitButtonContainer>
          <SignUpDialogFooter>
            Don't have an account?
            <Button disabled={form.isLoading || loading.isOpen} onClick={signInToggle.toggle}>Sign Up</Button>
          </SignUpDialogFooter>
        </Form>
      </SignUpDialog>
  }

}



// WEBPACK FOOTER //
// ./src/javascripts/checkout/CheckoutSignupDialog.jsx